// SPDX-License-Identifier: Elastic-2.0
/**
 * useDevices — TanStack Query hooks for the F-300 devices surface.
 *
 * Why these three hooks:
 *  - `useDevices()` — list query the dashboard subscribes to. 30s stale
 *    time (per global default) + window-focus refetch; the live source
 *    of truth in-between is the WebSocket push (see T-008 + T-024).
 *  - `useDevice(id)` — detail query for the device-control modal
 *    (T-024). Same cache that the list populates; the detail fetch
 *    fills in `diagnostics` which the list omits.
 *  - `useControlDevice()` — mutation for inline quick-controls. Applies
 *    an optimistic patch to the `["devices"]` cache so the toggle / slider
 *    snaps immediately, then reverts on error with a toast (T-011).
 *    On settle we invalidate the detail key so the modal — if open —
 *    re-reads the authoritative value (server may clamp e.g. brightness).
 *
 * Cache key shape (kept loose-public so siblings can read/write):
 *  - `["devices"]`         → DeviceListResponse
 *  - `["devices", id]`     → Device
 *
 * The WebSocket bridge (T-008 / T-030) reaches in via the same
 * `queryClient.setQueryData(["devices"], …)` API to push live state
 * mutations without a refetch. The state-flash animation in DeviceCard
 * keys off changes to `device.state` — the cache write is the trigger.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  DevicesService,
  type Device,
  type DeviceControlRequest,
  type DeviceControlResponse,
  type DeviceListResponse,
} from "@/api/gateway";

/** Query key for the device list. Exported so tests + WS bridge can target it. */
export const DEVICES_QUERY_KEY = ["devices"] as const;

/** Query key for a single device. */
export function deviceQueryKey(id: string): readonly [string, string] {
  return ["devices", id] as const;
}

/** Variables shape for `useControlDevice` — mirrors the generated request. */
export interface ControlDeviceVariables {
  deviceId: string;
  request: DeviceControlRequest;
  /**
   * Optimistic patch applied to the cached device's `state` field while
   * the request is in flight. When the action has no obvious local
   * projection (e.g. "lock" toggles a boolean), the caller can omit
   * this and `useControlDevice` will skip the optimistic update.
   */
  optimisticStatePatch?: Record<string, unknown>;
}

/** Internal context returned from `onMutate` so `onError` can revert. */
interface ControlDeviceContext {
  /** Snapshot of the list before the optimistic patch, for rollback. */
  previousList: DeviceListResponse | undefined;
  /** Snapshot of the detail-cached device (may be absent). */
  previousDetail: Device | undefined;
}

/**
 * List every device the user can see.
 *
 * Renders the dashboard's device grid. The result is grouped by
 * `groupDevicesByRoom` downstream — that ordering is a pure derivation
 * so it lives outside the query.
 */
export function useDevices(): UseQueryResult<DeviceListResponse, Error> {
  return useQuery<DeviceListResponse, Error>({
    queryKey: DEVICES_QUERY_KEY,
    queryFn: () => DevicesService.listDevicesDevicesGet(),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Fetch one device's full detail (incl. `diagnostics`).
 *
 * Used by the device-control modal (T-024). The list query won't return
 * diagnostics — that field is detail-only per F-300 to keep the list
 * payload small.
 */
export function useDevice(id: string | undefined): UseQueryResult<Device, Error> {
  return useQuery<Device, Error>({
    queryKey: id ? deviceQueryKey(id) : ["devices", "__noop__"],
    queryFn: () => DevicesService.getDeviceDevicesDeviceIdGet({ deviceId: id! }),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

/**
 * Apply an action to a device with an optimistic cache patch.
 *
 * Lifecycle:
 *  1. `onMutate` — snapshot the list cache, optimistically patch the
 *     target device's `state` (if `optimisticStatePatch` provided).
 *  2. `onError` — revert the list (+ detail) cache to the snapshot.
 *     The caller's `onError` (typically a toast trigger) runs after.
 *  3. `onSettled` — invalidate the detail query so the next read pulls
 *     the authoritative state. The list intentionally stays optimistic;
 *     the WebSocket push (T-008) will deliver the authoritative state
 *     within ~50ms and overwrite the cache directly.
 */
export function useControlDevice(): UseMutationResult<
  DeviceControlResponse,
  Error,
  ControlDeviceVariables,
  ControlDeviceContext
> {
  const queryClient = useQueryClient();

  return useMutation<
    DeviceControlResponse,
    Error,
    ControlDeviceVariables,
    ControlDeviceContext
  >({
    mutationFn: ({ deviceId, request }) =>
      DevicesService.controlDeviceDevicesDeviceIdControlPost({
        deviceId,
        requestBody: request,
      }),
    onMutate: async ({ deviceId, optimisticStatePatch }) => {
      // Cancel in-flight refetches so they can't clobber our patch.
      await queryClient.cancelQueries({ queryKey: DEVICES_QUERY_KEY });
      await queryClient.cancelQueries({ queryKey: deviceQueryKey(deviceId) });

      const previousList = queryClient.getQueryData<DeviceListResponse>(
        DEVICES_QUERY_KEY,
      );
      const previousDetail = queryClient.getQueryData<Device>(
        deviceQueryKey(deviceId),
      );

      if (optimisticStatePatch && previousList) {
        queryClient.setQueryData<DeviceListResponse>(DEVICES_QUERY_KEY, {
          ...previousList,
          devices: previousList.devices.map((d) =>
            d.id === deviceId
              ? { ...d, state: { ...d.state, ...optimisticStatePatch } }
              : d,
          ),
        });
      }
      if (optimisticStatePatch && previousDetail) {
        queryClient.setQueryData<Device>(deviceQueryKey(deviceId), {
          ...previousDetail,
          state: { ...previousDetail.state, ...optimisticStatePatch },
        });
      }

      return { previousList, previousDetail };
    },
    onError: (_err, { deviceId }, context) => {
      if (!context) return;
      if (context.previousList !== undefined) {
        queryClient.setQueryData(DEVICES_QUERY_KEY, context.previousList);
      }
      if (context.previousDetail !== undefined) {
        queryClient.setQueryData(deviceQueryKey(deviceId), context.previousDetail);
      }
    },
    onSettled: (_data, _err, { deviceId }) => {
      // Re-read the authoritative detail. The list cache stays as-is —
      // the WS push (T-008) is the live source of truth for the grid.
      queryClient.invalidateQueries({ queryKey: deviceQueryKey(deviceId) });
    },
  });
}
